import { useState } from "react";
import { User } from "../../models/interfaces/user";
import UserLatestMskScoreService from "../../utilities/services/user-latest-msk-score-service";
import UserService from "../../utilities/services/user-service";
import { BiologicalSexValue } from "../../models/biological-sex";
import { Utils } from "../../utilities/utils";
import { createLogger } from "../../utilities/logger";

const log = createLogger("MskRealAveragesTool");

interface GroupedAverages {
    ageRange: string;
    isFemale: boolean;
    mskTotal: number;
    mskCount: number;
    movementTotal: number;
    movementCount: number;
    breathingTotal: number;
    breathingCount: number;
    injuryHistoryTotal: number;
    injuryHistoryCount: number;
    sleepTotal: number;
    sleepCount: number;
    nutritionTotal: number;
    nutritionCount: number;
    behavioralTotal: number;
    behavioralCount: number;
    physicalActivityTotal: number;
    physicalActivityCount: number;
    bodyCompositionTotal: number;
    bodyCompositionCount: number;
    totalScores: number;
}

const COMPONENT_CLASS = "c-msk-real-averages-tool";

const getAgeRange = (age: number): string => {
    if (age <= 11) return "Under 12";
    if (age <= 15) return "12-15";
    if (age <= 19) return "16-19";
    if (age <= 24) return "20-24";
    if (age <= 29) return "25-29";
    if (age <= 34) return "30-34";
    if (age <= 39) return "35-39";
    if (age <= 44) return "40-44";
    if (age <= 49) return "45-49";
    if (age <= 54) return "50-54";
    if (age <= 59) return "55-59";
    if (age <= 64) return "60-64";
    if (age <= 69) return "65-69";
    if (age <= 74) return "70-74";
    return "over 74";
};

const MskRealAveragesTool: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<string>("");
    const [results, setResults] = useState<GroupedAverages[]>([]);
    const [totalUsers, setTotalUsers] = useState(0);

    const handleCalculate = async () => {
        setLoading(true);
        setError(null);
        setStatusMessage("Fetching MSK scores...");
        log.info("[MskRealAveragesTool] Starting...");

        try {
            const mskScores = await UserLatestMskScoreService.getAll();
            log.debug(`[MskRealAveragesTool] Fetched ${mskScores.length} MSK scores`);
            setStatusMessage(`Processing ${mskScores.length} MSK scores...`);

            if (mskScores.length === 0) {
                setResults([]);
                setLoading(false);
                return;
            }

            const uniqueUserIds = [...new Set(mskScores.map(s => s.userId).filter(Boolean))];
            log.debug(`[MskRealAveragesTool] Found ${uniqueUserIds.length} unique users`);

            const scoresNeedingUsers = mskScores.filter(s => !s.averageScores?.ageRange && !s.averageScores?.isFemale);
            const userIdsNeedingFetch = [...new Set(scoresNeedingUsers.map(s => s.userId).filter(Boolean))];
            log.debug(`[MskRealAveragesTool] ${userIdsNeedingFetch.length} scores need user data (averageScores is null)`);

            setStatusMessage(`Fetching ${userIdsNeedingFetch.length} users in batches...`);
            setTotalUsers(uniqueUserIds.length);

            log.debug("[MskRealAveragesTool] Fetching user data in batches...");
            const userMap = new Map<string, User>();

            const batchSize = 50;
            for (let i = 0; i < userIdsNeedingFetch.length; i += batchSize) {
                const batch = userIdsNeedingFetch.slice(i, i + batchSize);
                setStatusMessage(`Fetching users ${i + 1}-${Math.min(i + batchSize, userIdsNeedingFetch.length)} of ${userIdsNeedingFetch.length}...`);

                const userPromises = batch.map(userId => UserService.get(userId));
                const users = await Promise.all(userPromises);

                users.forEach((user, idx) => {
                    if (user) {
                        userMap.set(batch[idx], user);
                    }
                });

                log.debug(`[MskRealAveragesTool] Batch ${Math.floor(i / batchSize) + 1} complete, ${userMap.size} users cached`);
            }
            log.debug(`[MskRealAveragesTool] Cached ${userMap.size} users total`);

            const groupedMap = new Map<string, GroupedAverages>();

            for (let i = 0; i < mskScores.length; i++) {
                const score = mskScores[i];

                if (i % 500 === 0) {
                    log.debug(`[MskRealAveragesTool] Processing score ${i}/${mskScores.length}...`);
                }
                let ageRange: string;
                let isFemale: boolean;

                if (score.averageScores?.ageRange && score.averageScores?.isFemale !== undefined) {
                    ageRange = score.averageScores.ageRange;
                    isFemale = score.averageScores.isFemale;
                } else {
                    const user = userMap.get(score.userId);
                    if (!user?.birthdate || !user?.biologicalSex) {
                        continue;
                    }

                    const age = Utils.getAge(user.birthdate);
                    if (age === undefined) {
                        continue;
                    }

                    ageRange = getAgeRange(age);
                    isFemale = user.biologicalSex === BiologicalSexValue.Female;
                }

                const key = `${ageRange}-${isFemale}`;
                let grouped = groupedMap.get(key);
                if (!grouped) {
                    grouped = {
                        ageRange,
                        isFemale,
                        mskTotal: 0,
                        mskCount: 0,
                        movementTotal: 0,
                        movementCount: 0,
                        breathingTotal: 0,
                        breathingCount: 0,
                        injuryHistoryTotal: 0,
                        injuryHistoryCount: 0,
                        sleepTotal: 0,
                        sleepCount: 0,
                        nutritionTotal: 0,
                        nutritionCount: 0,
                        behavioralTotal: 0,
                        behavioralCount: 0,
                        physicalActivityTotal: 0,
                        physicalActivityCount: 0,
                        bodyCompositionTotal: 0,
                        bodyCompositionCount: 0,
                        totalScores: 0,
                    };
                    groupedMap.set(key, grouped);
                }

                grouped.totalScores++;

                if (score.percentage !== undefined && score.percentage !== null) {
                    grouped.mskTotal += score.percentage;
                    grouped.mskCount++;
                }

                if (score.movementScore?.percentage !== undefined && score.movementScore?.percentage !== null) {
                    grouped.movementTotal += score.movementScore.percentage;
                    grouped.movementCount++;
                }

                if (score.lifestyleScore?.breathingScore?.percentage !== undefined && score.lifestyleScore?.breathingScore?.percentage !== null) {
                    grouped.breathingTotal += score.lifestyleScore.breathingScore.percentage;
                    grouped.breathingCount++;
                }

                if (score.lifestyleScore?.injuryHistoryScore?.percentage !== undefined && score.lifestyleScore?.injuryHistoryScore?.percentage !== null) {
                    grouped.injuryHistoryTotal += score.lifestyleScore.injuryHistoryScore.percentage;
                    grouped.injuryHistoryCount++;
                }

                if (score.lifestyleScore?.sleepScore?.percentage !== undefined && score.lifestyleScore?.sleepScore?.percentage !== null) {
                    grouped.sleepTotal += score.lifestyleScore.sleepScore.percentage;
                    grouped.sleepCount++;
                }

                if (score.lifestyleScore?.nutritionScore?.percentage !== undefined && score.lifestyleScore?.nutritionScore?.percentage !== null) {
                    grouped.nutritionTotal += score.lifestyleScore.nutritionScore.percentage;
                    grouped.nutritionCount++;
                }

                if (score.lifestyleScore?.behavioralScore?.percentage !== undefined && score.lifestyleScore?.behavioralScore?.percentage !== null) {
                    grouped.behavioralTotal += score.lifestyleScore.behavioralScore.percentage;
                    grouped.behavioralCount++;
                }

                if (score.lifestyleScore?.physicalActivityScore?.percentage !== undefined && score.lifestyleScore?.physicalActivityScore?.percentage !== null) {
                    grouped.physicalActivityTotal += score.lifestyleScore.physicalActivityScore.percentage;
                    grouped.physicalActivityCount++;
                }

                if (score.lifestyleScore?.bodyCompositionScore?.percentage !== undefined && score.lifestyleScore?.bodyCompositionScore?.percentage !== null) {
                    grouped.bodyCompositionTotal += score.lifestyleScore.bodyCompositionScore.percentage;
                    grouped.bodyCompositionCount++;
                }
            }

            const results = Array.from(groupedMap.values()).map(g => ({
                ...g,
                mskTotal: g.mskCount > 0 ? g.mskTotal / g.mskCount : 0,
                movementTotal: g.movementCount > 0 ? g.movementTotal / g.movementCount : 0,
                breathingTotal: g.breathingCount > 0 ? g.breathingTotal / g.breathingCount : 0,
                injuryHistoryTotal: g.injuryHistoryCount > 0 ? g.injuryHistoryTotal / g.injuryHistoryCount : 0,
                sleepTotal: g.sleepCount > 0 ? g.sleepTotal / g.sleepCount : 0,
                nutritionTotal: g.nutritionCount > 0 ? g.nutritionTotal / g.nutritionCount : 0,
                behavioralTotal: g.behavioralCount > 0 ? g.behavioralTotal / g.behavioralCount : 0,
                physicalActivityTotal: g.physicalActivityCount > 0 ? g.physicalActivityTotal / g.physicalActivityCount : 0,
                bodyCompositionTotal: g.bodyCompositionCount > 0 ? g.bodyCompositionTotal / g.bodyCompositionCount : 0,
            }));

            results.sort((a, b) => {
                if (a.ageRange === "Under 12") return -1;
                if (b.ageRange === "Under 12") return 1;
                if (a.ageRange === "over 74") return 1;
                if (b.ageRange === "over 74") return -1;

                const aNum = parseInt(a.ageRange.split("-")[0]) || parseInt(a.ageRange.split(" ")[0]);
                const bNum = parseInt(b.ageRange.split("-")[0]) || parseInt(b.ageRange.split(" ")[0]);
                if (aNum !== bNum) return aNum - bNum;

                if (a.isFemale === b.isFemale) return 0;
                return a.isFemale ? 1 : -1;
            });

            setResults(results);
            setStatusMessage("");
            log.info(`[MskRealAveragesTool] Computed ${results.length} age/sex groups`);
        } catch (err) {
            log.error("[MskRealAveragesTool] Error calculating averages", {
                exception: err,
            });
            setError("Failed to calculate averages");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={COMPONENT_CLASS}>
            <h3>MSK Real Averages</h3>

            {!loading && results.length === 0 && !error && (
                <button onClick={handleCalculate} style={{ padding: "10px 20px", fontSize: "16px", cursor: "pointer" }}>
                    Calculate Real Averages
                </button>
            )}

            {loading && <p>{statusMessage || "Loading..."}</p>}
            {error && <p style={{ color: "red" }}>{error}</p>}

            {!loading && results.length > 0 && (
                <>
                    <p>Total unique users: {totalUsers}</p>
                    <table border={1} cellPadding={5} style={{ borderCollapse: "collapse", marginTop: "1rem" }}>
                        <thead>
                            <tr>
                                <th>Age Range</th>
                                <th>Sex</th>
                                <th>MSK Score</th>
                                <th>Movement</th>
                                <th>Breathing</th>
                                <th>Injury History</th>
                                <th>Sleep</th>
                                <th>Nutrition</th>
                                <th>Behavioral</th>
                                <th>Physical Activity</th>
                                <th>Body Composition</th>
                                <th>Count</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((r, idx) => (
                                <tr key={idx}>
                                    <td>{r.ageRange}</td>
                                    <td>{r.isFemale ? "Female" : "Male"}</td>
                                    <td>
                                        {r.mskCount > 0
                                            ? `${r.mskTotal.toFixed(1)} (${r.mskCount})`
                                            : "-"}
                                    </td>
                                    <td>
                                        {r.movementCount > 0
                                            ? `${r.movementTotal.toFixed(1)} (${r.movementCount})`
                                            : "-"}
                                    </td>
                                    <td>
                                        {r.breathingCount > 0
                                            ? `${r.breathingTotal.toFixed(1)} (${r.breathingCount})`
                                            : "-"}
                                    </td>
                                    <td>
                                        {r.injuryHistoryCount > 0
                                            ? `${r.injuryHistoryTotal.toFixed(1)} (${r.injuryHistoryCount})`
                                            : "-"}
                                    </td>
                                    <td>
                                        {r.sleepCount > 0
                                            ? `${r.sleepTotal.toFixed(1)} (${r.sleepCount})`
                                            : "-"}
                                    </td>
                                    <td>
                                        {r.nutritionCount > 0
                                            ? `${r.nutritionTotal.toFixed(1)} (${r.nutritionCount})`
                                            : "-"}
                                    </td>
                                    <td>
                                        {r.behavioralCount > 0
                                            ? `${r.behavioralTotal.toFixed(1)} (${r.behavioralCount})`
                                            : "-"}
                                    </td>
                                    <td>
                                        {r.physicalActivityCount > 0
                                            ? `${r.physicalActivityTotal.toFixed(1)} (${r.physicalActivityCount})`
                                            : "-"}
                                    </td>
                                    <td>
                                        {r.bodyCompositionCount > 0
                                            ? `${r.bodyCompositionTotal.toFixed(1)} (${r.bodyCompositionCount})`
                                            : "-"}
                                    </td>
                                    <td>{r.totalScores}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {!loading && results.length === 0 && !error && (
                <p>No MSK scores found.</p>
            )}
        </div>
    );
};

export default MskRealAveragesTool;